/**
 * ReportsAdminTab — admin widok zgłoszeń użytkowników (V2 2026-05-18).
 *
 * Lista + filtr po statusie + zmiana statusu + notatka admina.
 * platform_owner widzi wszystkie tenanty; admin tylko swój.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Skeleton } from '../ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { RefreshCw, Loader2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import {
  adminListUserReports, adminUpdateUserReport,
  downloadReportAttachment,
} from '../../lib/api';

const STATUSES = ['open', 'in_progress', 'awaiting_user_confirmation', 'resolved', 'wontfix', 'duplicate'];
const ST_LABEL = {
  open: 'Otwarte', in_progress: 'W trakcie',
  awaiting_user_confirmation: 'Czeka na usera', resolved: 'Rozwiązane',
  wontfix: 'Nie naprawimy', duplicate: 'Duplikat',
};
const ST_COLOR = {
  open: 'bg-amber-500/15 text-amber-300',
  in_progress: 'bg-blue-500/15 text-blue-300',
  awaiting_user_confirmation: 'bg-purple-500/15 text-purple-300',
  resolved: 'bg-emerald-500/15 text-emerald-300',
  wontfix: 'bg-zinc-500/15 text-zinc-300',
  duplicate: 'bg-zinc-500/15 text-zinc-300',
};

function fmt(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('pl-PL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return String(s).slice(0, 16); }
}

function Row({ r, onChanged }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(r.status);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await adminUpdateUserReport(r.id, {
        status: status !== r.status ? status : undefined,
        admin_note: note.trim() || undefined,
      });
      toast.success('Zaktualizowano');
      setNote('');
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Błąd zapisu');
    } finally { setBusy(false); }
  };

  return (
    <Card>
      <CardContent className="p-3">
        <button className="w-full text-left" onClick={() => setOpen(!open)}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={`text-[10px] ${ST_COLOR[r.status] || ''}`}>
                  {ST_LABEL[r.status] || r.status}
                </Badge>
                <Badge variant="outline" className="text-[10px]">{r.category}</Badge>
                {r.severity === 'critical' && (
                  <Badge className="text-[10px] bg-red-500/15 text-red-300">krytyczny</Badge>
                )}
                <span className="text-[11px] text-muted-foreground">{r.user_email}</span>
                <span className="text-[11px] text-muted-foreground">· {r.module} · {fmt(r.created_at)}</span>
              </div>
              <div className="font-medium text-sm mt-1">{r.title}</div>
            </div>
            <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
          </div>
        </button>

        {open && (
          <div className="border-t border-border pt-3 mt-2 space-y-3">
            <div className="text-xs text-muted-foreground whitespace-pre-wrap">{r.description}</div>
            <div className="text-[11px] text-muted-foreground">
              URL: {r.context?.url || '—'} · {r.context?.screen || ''} · ID: {r.id}
            </div>
            {r.context?.user_agent && (
              <div className="text-[11px] text-muted-foreground">UA: {r.context.user_agent}</div>
            )}
            {(r.context?.failed_requests || []).length > 0 && (
              <div className="bg-red-500/10 rounded p-2 text-[11px] space-y-0.5">
                <div className="font-medium text-red-300">Nieudane zapytania:</div>
                {r.context.failed_requests.map((f, i) => (
                  <div key={i} className="font-mono">
                    {f.method} {f.url} → <span className="text-red-300">{f.status}</span>
                    {f.detail ? ` · ${f.detail}` : ''}
                  </div>
                ))}
              </div>
            )}
            {(r.context?.js_errors || []).length > 0 && (
              <div className="bg-amber-500/10 rounded p-2 text-[11px] space-y-1">
                <div className="font-medium text-amber-300">Błędy JS:</div>
                {r.context.js_errors.map((e, i) => (
                  <div key={i} className="font-mono">
                    <div>[{e.kind}] {e.message}</div>
                    {e.stack && <div className="text-muted-foreground whitespace-pre-wrap">{e.stack}</div>}
                  </div>
                ))}
              </div>
            )}

            {(r.attachments || []).length > 0 && (
              <div className="bg-blue-500/10 rounded p-2 text-[11px] space-y-1">
                <div className="font-medium text-blue-300">📎 Załączniki ({r.attachments.length}):</div>
                {r.attachments.map((a, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <a
                      href={downloadReportAttachment(r.id, a.filename)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline truncate"
                    >
                      {a.original_filename || a.filename}
                    </a>
                    <span className="text-muted-foreground">
                      {a.mime} · {((a.size_bytes || 0) / 1024).toFixed(0)} KB
                    </span>
                  </div>
                ))}
              </div>
            )}

            {(r.comments || []).length > 0 && (
              <div className="space-y-1">
                {r.comments.map((c, i) => (
                  <div key={i} className={`text-xs rounded p-2 ${c.by === 'user' ? 'bg-muted/40' : 'bg-blue-500/10'}`}>
                    <span className="font-medium">{c.by === 'user' ? r.user_email : 'Zespół'}</span>
                    <span className="text-muted-foreground"> · {fmt(c.at)}</span>
                    <div className="mt-0.5">{c.text}</div>
                  </div>
                ))}
              </div>
            )}
            {(r.admin_notes || []).length > 0 && (
              <div className="space-y-1">
                {r.admin_notes.map((n, i) => (
                  <div key={i} className="text-xs rounded p-2 bg-amber-500/10">
                    <span className="font-medium">{n.by}</span>
                    <span className="text-muted-foreground"> · {fmt(n.at)}</span>
                    <div className="mt-0.5">{n.text}</div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center gap-2 flex-wrap">
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-8 w-[200px] text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map(s => <SelectItem key={s} value={s}>{ST_LABEL[s]}</SelectItem>)}
                </SelectContent>
              </Select>
              <span className="text-[11px] text-muted-foreground">
                „Czeka na usera” = poprosi o potwierdzenie naprawy
              </span>
            </div>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Notatka admina (opcjonalnie)..." rows={2} className="text-xs" />
            <Button size="sm" onClick={save} disabled={busy}>
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
              Zapisz zmiany
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function ReportsAdminTab() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      const r = await adminListUserReports(params);
      setItems(r.items || []);
      setCounts(r.by_status || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Nie udało się załadować zgłoszeń');
    } finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3 pt-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="h-8 w-[200px] text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Wszystkie statusy</SelectItem>
            {STATUSES.map(s => (
              <SelectItem key={s} value={s}>
                {ST_LABEL[s]}{counts[s] != null ? ` (${counts[s]})` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={load} className="gap-1">
          <RefreshCw className="w-3.5 h-3.5" /> Odśwież
        </Button>
        <span className="text-xs text-muted-foreground ml-auto">{items.length} zgłoszeń</span>
      </div>

      {loading ? (
        <div className="space-y-2">{[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}</div>
      ) : items.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-muted-foreground text-sm">
          Brak zgłoszeń{filter !== 'all' ? ` w statusie „${ST_LABEL[filter]}”` : ''}.
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {items.map(r => <Row key={r.id} r={r} onChanged={load} />)}
        </div>
      )}
    </div>
  );
}
